/**
 * BoardInlineView — 채널 내 게시판 인라인 뷰
 *
 * 기존 Board 모듈의 PostListView + PostDetailOverlay를
 * 채널 탭 내에서 임베드하여 사용합니다.
 * 자체 BoardContext를 제공하며, 게시판 생성/게시글 작성 모달을 내장합니다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Plus, PenLine, ChevronDown,
  FileText, Bell, BellDot, MessageSquareText, BookOpen, ListTodo,
  Settings, PanelLeft, LayoutDashboard,
} from 'lucide-react';
import {
  Board, BoardType, Post,
  resolveDisplayStyle, canManageBoard,
} from '../../models/Board';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { useResponsive } from '../../hooks/useResponsive';
import BoardEditModal from '../board/BoardEditModal';
import { cn } from '../../lib/utils';
import { BoardContext } from '../../context/BoardContext';
import {
  fetchBoards, fetchPost, fetchPosts,
  searchPosts as searchPostsService,
  createBoard,
} from '../../services/BoardService';
import PostListView from '../board/PostListView';
import BoardDashboardPage, { SkeletonRows } from '../board/BoardDashboardPage';
import BoardNotifyAllSettingsModal from '../board/BoardNotifyAllSettingsModal';
import PostDetailOverlay from '../board/PostDetailOverlay';
import PostCreateModal from '../board/PostCreateModal';
import WikiSidebar from '../board/WikiSidebar';
import BoardCreateModal from '../board/BoardCreateModal';
import BoardOnboarding from '../board/BoardOnboarding';
import WikiOnboarding from '../board/WikiOnboarding';
import WikiPageCreateModal from '../board/WikiPageCreateModal';
import { WikiPageEmitter } from '../board/WikiPageEmitter';
import { My } from '../../services/My';
import { Button } from '../../components/ui/button';

interface BoardInlineViewProps {
  boardId: string | null;
  workspaceId: string;
  roomId: string;
  /** 채널 방장 UID — 채널 게시판 생성은 방장만 가능하므로 생성 버튼/모달 가드용 */
  channelOwnerId?: string;
  /** 게시판 선택/생성 후 새 boardId로 전환 (RoomPage의 selectedBoardId 갱신). null = 미선택(대시보드) */
  onBoardSelect?: (boardId: string | null) => void;
  /** 메시지팝오버 "위키 게시판 시작하기"로 진입했을 때 채팅 메시지를 함께 전달.
   *  WikiOnboarding 시작하기 → 위키 게시판 생성 → 이 메시지로 즉시 모달 띄움 */
  pendingWikiChat?: { threadText: string; threadMessageId: string; sourceScope: 'message' | 'thread' } | null;
  /** pending wiki chat 처리 완료 후 호출 (RoomPage state 초기화) */
  onPendingWikiChatConsumed?: () => void;
  /** 게시글 상세 진입/탈출 알림 — RoomPage 가 우측 사이드바 자동 숨김 등에 활용 */
  onPostDetailChange?: (open: boolean) => void;
  /** AutoMessage('post-created') 카드 클릭 등으로 외부에서 특정 게시글 열기 요청 */
  pendingPostOpen?: { boardId: string; postId: string } | null;
  /** pendingPostOpen 처리 완료 후 RoomPage state 초기화 */
  onPendingPostOpenConsumed?: () => void;
}

/** 게시판 유형별 아이콘 */
function boardIcon(type: BoardType) {
  switch (type) {
    case 'notice': return Bell;
    case 'qna': return MessageSquareText;
    case 'wiki': return BookOpen;
    case 'issue': return ListTodo;
    default: return FileText;
  }
}

const BoardInlineView: React.FC<BoardInlineViewProps> = ({
  boardId,
  workspaceId,
  roomId,
  channelOwnerId,
  onBoardSelect,
  pendingWikiChat,
  onPendingWikiChatConsumed,
  onPostDetailChange,
  pendingPostOpen,
  onPendingPostOpenConsumed,
}) => {
  // 자체 Context 상태 (모듈 BoardProvider와 독립)
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(false);
  // 보드 목록 로드 완료 여부 — 로드 전엔 온보딩/대시보드 대신 로딩 표시 (깜빡 방지)
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 모달 상태
  const [showPostCreate, setShowPostCreate] = useState(false);
  const [showBoardCreate, setShowBoardCreate] = useState(false);
  // 인라인 검색
  // 게시판 선택 popover (제목 오른쪽 chevron)
  const [boardPopoverOpen, setBoardPopoverOpen] = useState(false);

  // 위키 온보딩 (시작하기) — pendingWikiChat 있을 때 채널에 위키 없으면 표시
  const [wikiActivating, setWikiActivating] = useState(false);
  // 위키 페이지 생성 모달 (시작하기 직후 바로 띄우기)
  const [wikiPageModal, setWikiPageModal] = useState<{ board: Board; threadText: string; threadMessageId: string; sourceScope: 'message' | 'thread' } | null>(null);

  // 현재 선택된 게시판 설정 모달 — 삭제는 모달 안의 '위험 영역'에서 수행
  const [showBoardEdit, setShowBoardEdit] = useState(false);
  const [showNotifyAll, setShowNotifyAll] = useState(false);

  /** 위키 게시판: 모바일/태블릿 좌측 페이지 목록 드로어 열림 여부 */
  const [wikiSidebarOpen, setWikiSidebarOpen] = useState(false);
  // 컨테이너 폭 기준 — 채널 분할 시 RoomPage 가 패널 폭을 ResponsiveOverrideContext 로 주입
  const { isMobile } = useResponsive();

  // 권한 체크 — 현재 사용자가 선택된 게시판을 관리할 수 있는지
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const canManageSelectedBoard = selectedBoard
    ? canManageBoard(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids)
    : false;
  /** 채널 게시판 생성은 채널 방장만 — 생성 버튼/모달 진입 가드 */
  const isChannelOwner = !!channelOwnerId && channelOwnerId === My.user?.id;

  // 현재 채널에 연결된 게시판만 추출 (popover 목록용)
  const channelBoards = useMemo(
    () => boards.filter(b => b.scope === 'channel' && b.channel_id === roomId),
    [boards, roomId],
  );
  const channelWikiBoard = useMemo(
    () => channelBoards.find(b => b.type === 'wiki') ?? null,
    [channelBoards],
  );

  // 채널 대시보드 데이터 스코프 — 이 채널 게시판들로 한정
  const dashScopeBoardIds = useMemo(() => channelBoards.map(b => b.id), [channelBoards]);

  // 채널 대시보드에서 게시글/게시판으로 진입 (history 대신 인라인 상태 전환)
  const handleDashNavPost = useCallback((bId: string, postId: string) => {
    const board = boards.find(b => b.id === bId);
    if (board) { setSelectedBoard(board); onBoardSelect?.(board.id); }
    fetchPost(postId).then(p => { if (p) setSelectedPost(p); }).catch(console.error);
  }, [boards, onBoardSelect]);
  const handleDashNavBoard = useCallback((bId: string) => {
    const board = boards.find(b => b.id === bId);
    if (board) { setSelectedBoard(board); onBoardSelect?.(board.id); }
  }, [boards, onBoardSelect]);

  /** 위키 온보딩 "시작하기" — 위키 게시판 자동 생성 + pendingWikiChat 있으면 즉시 모달 */
  const handleWikiActivate = useCallback(async () => {
    if (!My.user || wikiActivating) return;
    setWikiActivating(true);
    try {
      const wikiBoard = channelWikiBoard ?? await createBoard({
        workspace_id: workspaceId,
        name: t`Wiki`,
        description: t`Team wiki — accumulate content agreed in chat as pages`,
        type: 'wiki',
        scope: 'channel',
        channel_id: roomId,
        owner_id: My.user.id,
      });
      // 보드 목록 갱신 + 선택
      const updated = await fetchBoards(workspaceId);
      setBoards(updated);
      onBoardSelect?.(wikiBoard.id);
      // pendingWikiChat 있으면 즉시 위키 페이지 생성 모달
      if (pendingWikiChat) {
        setWikiPageModal({
          board: wikiBoard,
          threadText: pendingWikiChat.threadText,
          threadMessageId: pendingWikiChat.threadMessageId,
          sourceScope: pendingWikiChat.sourceScope,
        });
        onPendingWikiChatConsumed?.();
      }
    } catch (err) {
      console.error('[BoardInlineView] 위키 게시판 생성 실패:', err);
    } finally {
      setWikiActivating(false);
    }
  }, [channelWikiBoard, workspaceId, roomId, pendingWikiChat, onBoardSelect, onPendingWikiChatConsumed, wikiActivating]);

  const refreshBoards = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      setError(null);
      const list = await fetchBoards(workspaceId);
      setBoards(list);
    } catch (err) {
      console.error('[BoardInlineView] 게시판 목록 로드 실패:', err);
      setError(t`Failed to load boards`);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const refreshPosts = useCallback(async (bid: string, page?: number) => {
    try {
      setLoading(true);
      setError(null);
      if (searchQuery.trim()) {
        const result = await searchPostsService(bid, searchQuery.trim(), { page: page ?? 0 });
        setPosts(result.posts);
        setTotalPosts(result.total);
      } else {
        const result = await fetchPosts(bid, { page: page ?? 0 });
        setPosts(result.posts);
        setTotalPosts(result.total);
      }
    } catch (err) {
      console.error('[BoardInlineView] 게시글 로드 실패:', err);
      setError(t`Failed to load posts`);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // 채널 변경 시 보드 목록 재로딩 표시 (채팅처럼 로딩 먼저 → 온보딩/대시보드)
  useEffect(() => { setBoardsLoaded(false); }, [roomId]);

  // boardId 변경 또는 채널/워크스페이스 변경 시: 전체 게시판 목록 로드 + 선택 게시판 결정
  // boardId가 비어 있고 이 채널에 속한 게시판이 있으면 자동으로 첫 번째 게시판 선택
  useEffect(() => {
    if (!workspaceId) return;

    fetchBoards(workspaceId).then(boards => {
      setBoards(boards);

      if (boardId) {
        const board = boards.find(b => b.id === boardId);
        if (board) {
          setSelectedBoard(board);
          return;
        }
      }

      // boardId 가 없으면 자동선택하지 않고 미선택(대시보드) 상태 유지
      setSelectedBoard(null);
      setPosts([]);
      setTotalPosts(0);
    }).catch(console.error).finally(() => setBoardsLoaded(true));
  }, [boardId, workspaceId, roomId]);

  // WikiPageEmitter 'created' — 채팅→위키 변환 모달 저장 직후
  // 현재 채널의 위키 게시판 페이지 목록을 즉시 리프레시
  useEffect(() => {
    const handler = (createdRoomId: string, createdBoardId: string) => {
      if (createdRoomId !== roomId) return;
      // 보드 목록 + 현재 선택된 보드의 글 목록 모두 갱신
      fetchBoards(workspaceId).then(setBoards).catch(() => {});
      // 위키 보드가 selected가 아니면 선택 전환 (보드가 방금 새로 만들어졌을 수도)
      if (selectedBoard?.id !== createdBoardId) {
        onBoardSelect?.(createdBoardId);
      } else {
        refreshPosts(createdBoardId);
      }
    };
    WikiPageEmitter.on('created', handler);
    return () => { WikiPageEmitter.removeListener('created', handler); };
  }, [roomId, workspaceId, selectedBoard?.id, onBoardSelect, refreshPosts]);

  // 선택된 게시판 변경 또는 검색어 변경 시 게시글 로드 (searchQuery 누락 시 검색어 넣어도 재조회 안 됨)
  useEffect(() => {
    if (selectedBoard) {
      refreshPosts(selectedBoard.id);
    }
  }, [selectedBoard?.id, searchQuery]);

  // 게시글 상세 진입/탈출 알림 — RoomPage 가 우측 사이드바 자동 숨김 등에 활용
  useEffect(() => {
    onPostDetailChange?.(!!selectedPost);
  }, [selectedPost, onPostDetailChange]);

  // AutoMessage('post-created'/'wiki-page-created') 카드 클릭 처리:
  // pendingPostOpen 변화 시 → 해당 게시판으로 전환 후 게시글 로드 → selectedPost 설정
  useEffect(() => {
    if (!pendingPostOpen) return;
    const target = pendingPostOpen;
    const targetBoard = boards.find(b => b.id === target.boardId);
    if (!targetBoard) {
      // 보드 목록이 아직 로드되지 않았거나 권한 없음 — 다음 라운드에 재시도
      return;
    }
    // 보드 전환 (RoomPage selectedBoardId 갱신용 콜백 호출)
    if (boardId !== target.boardId) {
      onBoardSelect?.(target.boardId);
    }
    // 게시글 로드 → 상세 열기
    fetchPost(target.postId).then(p => {
      if (p) setSelectedPost(p);
    }).catch(err => {
      console.warn('[BoardInlineView] pendingPostOpen 게시글 로드 실패:', err);
    });
    onPendingPostOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPostOpen, boards.length]);

  // BoardContext value — 빈 상태에서도 Provider가 감싸야 BoardCreateModal이 동작
  const contextValue = {
    boards,
    setBoards,
    selectedBoard,
    setSelectedBoard: (board: Board | null) => {
      setSelectedBoard(board);
      // 게시판 생성 후 새 게시판이 선택되면 부모에게 알려줌
      if (board) {
        onBoardSelect?.(board.id);
      }
    },
    selectedPost,
    setSelectedPost,
    posts,
    setPosts,
    totalPosts,
    setTotalPosts,
    loading,
    setLoading,
    refreshBoards,
    refreshPosts,
    searchQuery,
    setSearchQuery,
    // 채널 인라인 뷰에는 즐겨찾기 토글 UI 가 없어 기본값만 충족. 게시판 모듈(BoardProvider)에서만 실제 동작.
    pinnedIds: [] as string[],
    togglePin: async () => false,
    error,
  };

  // 위키 페이지 생성 모달 — 두 브랜치 공통으로 렌더 (시작하기 직후 자동 띄움)
  const wikiPageModalElement = wikiPageModal && (
    <WikiPageCreateModal
      open={wikiPageModal !== null}
      onOpenChange={(open) => { if (!open) setWikiPageModal(null); }}
      board={wikiPageModal.board}
      threadText={wikiPageModal.threadText}
      roomId={roomId}
      threadMessageId={wikiPageModal.threadMessageId}
      sourceScope={wikiPageModal.sourceScope}
    />
  );

  // 통합 알림 설정 모달 — 채널 게시판들로 한정 (게시판 어디서든 헤더 맨 오른쪽에서 진입)
  const notifyModalElement = (
    <BoardNotifyAllSettingsModal
      open={showNotifyAll}
      onClose={() => setShowNotifyAll(false)}
      workspaceId={workspaceId}
      boardIds={dashScopeBoardIds}
    />
  );

  // 채널에 위키 게시판이 없는데 메시지팝오버 "위키 게시판 시작하기"로 진입한 경우
  // → 일반 BoardOnboarding 대신 WikiOnboarding 표시
  if (pendingWikiChat && !channelWikiBoard) {
    return (
      <BoardContext.Provider value={contextValue}>
        <WikiOnboarding
          onActivate={handleWikiActivate}
          activating={wikiActivating}
          hasPendingChat
        />
        {wikiPageModalElement}
      </BoardContext.Provider>
    );
  }

  // boards 로드 전 → 대시보드 스켈레톤 (온보딩/대시보드 깜빡 방지, 채팅창처럼)
  if (!boardId && !boardsLoaded) {
    return (
      <BoardContext.Provider value={contextValue}>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* 헤더 placeholder — 대시보드 경량 헤더 자리 */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border shrink-0">
            <div className="size-4 rounded bg-muted animate-pulse" />
            <div className="h-3.5 w-20 rounded bg-muted animate-pulse" />
          </div>
          <SkeletonRows count={6} />
        </div>
      </BoardContext.Provider>
    );
  }

  // 게시판은 있으나 미선택 → 채널 대시보드 (메인 BoardPage 의 대시보드 패턴을 채널 스코프로)
  if (!boardId && channelBoards.length > 0) {
    return (
      <BoardContext.Provider value={contextValue}>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* 경량 헤더 — 대시보드 라벨 + 보드 선택 popover */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border shrink-0">
            <Popover open={boardPopoverOpen} onOpenChange={setBoardPopoverOpen} modal>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 -ml-1 px-1 py-0.5 min-w-0 rounded-md text-muted-foreground hover:bg-muted/60 transition-colors"
                  title={t`Switch board`}
                >
                  <LayoutDashboard className="size-4 text-primary shrink-0" />
                  <span className="text-label font-medium text-foreground truncate"><Trans>Dashboard</Trans></span>
                  <ChevronDown className="size-3.5 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={4} className="w-52 p-1.5">
                {channelBoards.map(board => {
                  const Icon = boardIcon(board.type);
                  return (
                    <button
                      key={board.id}
                      onClick={() => { onBoardSelect?.(board.id); setBoardPopoverOpen(false); }}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-label text-foreground/80 hover:bg-muted/60 transition-colors"
                    >
                      <Icon className="size-3.5 shrink-0" />
                      <span className="truncate flex-1 text-left">{board.name}</span>
                      {board.post_count > 0 && (
                        <span className="text-meta-sm tabular-nums text-muted-foreground/60">{board.post_count}</span>
                      )}
                    </button>
                  );
                })}
                {isChannelOwner && <>
                <div className="border-t border-border my-1" />
                <button
                  onClick={() => { setBoardPopoverOpen(false); setShowBoardCreate(true); }}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-label text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                >
                  <Plus className="size-3.5" />
                  <Trans>Create Board</Trans>
                </button>
                </>}
              </PopoverContent>
            </Popover>
            <button
              type="button"
              onClick={() => setShowNotifyAll(true)}
              className="ml-auto size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors shrink-0"
              title={t`My notification settings`}
              aria-label={t`My notification settings`}
            >
              <BellDot className="size-4" />
            </button>
          </div>
          <BoardDashboardPage
            channelMode
            scopeBoardIds={dashScopeBoardIds}
            onNavigatePost={handleDashNavPost}
            onNavigateBoard={handleDashNavBoard}
          />
        </div>
        <BoardCreateModal
          open={showBoardCreate}
          onOpenChange={setShowBoardCreate}
          initialScope="channel"
          initialChannelId={roomId}
        />
        {wikiPageModalElement}
        {notifyModalElement}
      </BoardContext.Provider>
    );
  }

  // 게시판이 없는 경우: 온보딩 안내 (BoardContext.Provider 안에서 렌더)
  if (!boardId) {
    return (
      <BoardContext.Provider value={contextValue}>
        <BoardOnboarding onCreateBoard={isChannelOwner ? () => setShowBoardCreate(true) : undefined} />

        {/* 게시판 생성 모달 — scope=channel, channelId=roomId 고정 */}
        <BoardCreateModal
          open={showBoardCreate}
          onOpenChange={setShowBoardCreate}
          initialScope="channel"
          initialChannelId={roomId}
        />
        {wikiPageModalElement}
      </BoardContext.Provider>
    );
  }

  return (
    <BoardContext.Provider value={contextValue}>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* 게시판 인라인 툴바 — 상세 진입 후에도 유지하여 좌측 사이드바의 수직 위치를 고정 */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border shrink-0">
          {(
            <>
              {/* 위키 사이드바 토글 — 좁은 폭(컨테이너 기준)에서만 노출 */}
              {selectedBoard?.type === 'wiki' && (
                <button
                  onClick={() => setWikiSidebarOpen(true)}
                  className={cn('p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0', !isMobile && 'hidden')}
                  title={t`Page list`}
                  aria-label={t`Open page list`}
                >
                  <PanelLeft className="size-3.5" />
                </button>
              )}

              {/* 게시판명 + 게시글 수 + 게시판 선택/추가 popover (chevron) */}
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <Popover open={boardPopoverOpen} onOpenChange={setBoardPopoverOpen} modal>
                  <PopoverTrigger asChild>
                    <button
                      className="flex items-center gap-1 min-w-0 -ml-1 px-1 py-0.5 rounded-md text-muted-foreground hover:bg-muted/60 transition-colors"
                      title={t`Switch board`}
                    >
                      <span className="text-label font-medium text-foreground/80 truncate">
                        {selectedBoard?.name}
                        {totalPosts > 0 && (
                          <span className="text-muted-foreground/60 ml-1.5 text-meta tabular-nums">
                            ({totalPosts})
                          </span>
                        )}
                      </span>
                      <ChevronDown className="size-3.5 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={4}
                    className="w-52 p-1.5"
                  >
                    {/* 대시보드(홈) — 미선택 상태로 전환 */}
                    <button
                      onClick={() => {
                        onBoardSelect?.(null);
                        setSelectedBoard(null);
                        setSelectedPost(null);
                        setBoardPopoverOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-label text-foreground/80 hover:bg-muted/60 transition-colors"
                    >
                      <LayoutDashboard className="size-3.5 shrink-0" />
                      <span className="truncate flex-1 text-left"><Trans>Dashboard</Trans></span>
                    </button>
                    <div className="border-t border-border my-1" />
                    {channelBoards.map(board => {
                      const Icon = boardIcon(board.type);
                      const isSelected = board.id === selectedBoard?.id;
                      return (
                        <button
                          key={board.id}
                          onClick={() => {
                            onBoardSelect?.(board.id);
                            setBoardPopoverOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-label transition-colors',
                            isSelected
                              ? 'bg-primary/8 text-primary font-medium'
                              : 'text-foreground/80 hover:bg-muted/60'
                          )}
                        >
                          <Icon className="size-3.5 shrink-0" />
                          <span className="truncate flex-1 text-left">{board.name}</span>
                          {board.post_count > 0 && (
                            <span className="text-meta-sm tabular-nums text-muted-foreground/60">
                              {board.post_count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {isChannelOwner && <>
                    {channelBoards.length > 0 && <div className="border-t border-border my-1" />}
                    <button
                      onClick={() => {
                        setBoardPopoverOpen(false);
                        setShowBoardCreate(true);
                      }}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-label text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                    >
                      <Plus className="size-3.5" />
                      <Trans>Create Board</Trans>
                    </button>
                    </>}
                  </PopoverContent>
                </Popover>
              </div>

              {/* 게시판 설정 — 관리 권한 있을 때만 표시. 권한 표시는 헤더 타이틀 옆에서 일괄. */}
              {selectedBoard && canManageSelectedBoard && (
                <button
                  onClick={() => setShowBoardEdit(true)}
                  className="p-1 rounded-md text-admin/70 hover:text-admin hover:bg-admin/10 transition-colors"
                  title={t`Board settings`}
                >
                  <Settings className="size-3.5" />
                </button>
              )}


              {/* 새 글 작성 */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPostCreate(true)}
                className="h-7 gap-1 text-label px-2"
              >
                <PenLine className="size-3.5" />
                <Trans>Write</Trans>
              </Button>

              {/* 알림 설정 — 맨 오른쪽 (게시판 어디서든 접근) */}
              <button
                type="button"
                onClick={() => setShowNotifyAll(true)}
                className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-muted/60 transition-colors shrink-0"
                title={t`My notification settings`}
                aria-label={t`My notification settings`}
              >
                <BellDot className="size-3.5" />
              </button>
            </>
          )}
        </div>

        {/* 게시글 상세 또는 목록 — 위키 게시판은 좌측 페이지 목록 사이드바와 동시 렌더 */}
        <div className="flex-1 min-h-0 relative flex">
          {selectedBoard?.type === 'wiki' && (
            <WikiSidebar
              isOpen={wikiSidebarOpen}
              onClose={() => setWikiSidebarOpen(false)}
            />
          )}
          <div className="flex-1 min-w-0 relative">
            {/* 검색 중에는 상세 대신 검색 결과 목록(+페이징) — 위키는 항상 페이지가 선택돼 있어 검색 결과가 가려지던 문제 해소 */}
            {selectedPost && !searchQuery.trim() ? (
              <PostDetailOverlay />
            ) : (
              <PostListView displayStyle={(() => {
                if (!selectedBoard) return 'discourse';
                // 관리자(board.display_style) 우선. stale userPref 무시 — BoardPage 와 동일 정책.
                return resolveDisplayStyle(selectedBoard.display_style ?? null, false);
              })()} />
            )}
          </div>
        </div>

        {/* 게시글 작성 모달 */}
        <PostCreateModal open={showPostCreate} onOpenChange={setShowPostCreate} />

        {/* 게시판 생성 모달 */}
        <BoardCreateModal
          open={showBoardCreate}
          onOpenChange={setShowBoardCreate}
          initialScope="channel"
          initialChannelId={roomId}
        />

        {/* 위키 페이지 생성 모달 — 시작하기 직후 자동 띄움 */}
        {wikiPageModalElement}
        {notifyModalElement}

        {/* 게시판 설정 모달 */}
        {/* 게시판 설정 모달 — 삭제는 모달 내부 '위험 영역'에서 처리 */}
        {selectedBoard && (
          <BoardEditModal
            open={showBoardEdit}
            onOpenChange={setShowBoardEdit}
            board={selectedBoard}
          />
        )}
      </div>
    </BoardContext.Provider>
  );
};

export default BoardInlineView;
